import React, { useCallback, useEffect, useRef, useState } from "react";
import { adminFetch } from "../adminApi";
import "./admin-photo-enhance.css";

/**
 * Photo enhancing for product photos (Admin → Products → Enhance photos, and
 * "Adjust" on a photo in the product editor).
 *
 *   POST /api/admin/images/preview      {product_id, image_index, adjustments} → JPEG
 *   POST /api/admin/images/enhance-jobs {product_ids, scope, image_index?, adjustments}
 *   GET  /api/admin/images/enhance-jobs/{id}
 *
 * Every render starts from the photo's untouched original, so new settings
 * replace earlier ones rather than stacking, and all-zero settings revert.
 */

export const SLIDER_GROUPS = [
  {
    title: "Light",
    sliders: [
      { key: "exposure", label: "Brightness" },
      { key: "contrast", label: "Contrast" },
      { key: "highlights", label: "Highlights" },
      { key: "shadows", label: "Shadows" },
    ],
  },
  {
    title: "Colour & presence",
    sliders: [
      { key: "clarity", label: "Clarity" },
      { key: "vibrance", label: "Vibrance" },
      { key: "saturation", label: "Saturation" },
      { key: "warmth", label: "Warmth" },
    ],
  },
  { title: "Detail", sliders: [{ key: "sharpen", label: "Sharpen", min: 0 }] },
];
const SLIDER_KEYS = SLIDER_GROUPS.flatMap((g) => g.sliders.map((s) => s.key));

export const EMPTY_ADJUSTMENTS = SLIDER_KEYS.reduce(
  (acc, key) => ({ ...acc, [key]: 0 }),
  { auto: false }
);

/* Same recipes as backend/services/image_enhance.py PRESETS */
export const PRESETS = [
  { key: "jewellery", label: "Jewellery sparkle", values: { auto: true, clarity: 25, vibrance: 30, sharpen: 20, highlights: -15 } },
  { key: "natural", label: "Natural (auto)", values: { auto: true } },
  { key: "brighten", label: "Brighten", values: { exposure: 20, shadows: 25 } },
];

export const isIdentity = (adj) =>
  !adj.auto && SLIDER_KEYS.every((k) => !Number(adj[k]));

export const photoEnhance = (image) =>
  image && image.enhance && typeof image.enhance === "object" ? image.enhance : null;

/** Starts a job and polls it until it finishes. onFinish(job) runs once. */
export function useEnhanceJob(navigate, onFinish) {
  const [job, setJob] = useState(null);
  const finishRef = useRef(onFinish);
  finishRef.current = onFinish;

  useEffect(() => {
    if (!job || job.status !== "running") return undefined;
    const timer = setTimeout(async () => {
      try {
        const res = await adminFetch(`/api/admin/images/enhance-jobs/${job.id}`, { navigate });
        if (!res.ok) throw new Error("Lost track of the photo job");
        const next = await res.json();
        setJob(next);
        if (next.status !== "running" && finishRef.current) finishRef.current(next);
      } catch (err) {
        if (!err.isAuthError) {
          const failed = { ...job, status: "failed", failure: err.message };
          setJob(failed);
          if (finishRef.current) finishRef.current(failed);
        }
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [job, navigate]);

  const start = useCallback(
    async (body) => {
      const res = await adminFetch("/api/admin/images/enhance-jobs", {
        method: "POST",
        navigate,
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Couldn't start the photo job");
      const started = {
        id: data.job_id,
        status: "running",
        total: data.total,
        done: 0,
        errors: [],
        revert: isIdentity(body.adjustments || {}),
      };
      setJob(started);
      return started;
    },
    [navigate]
  );

  return { job, start, clear: () => setJob(null) };
}

export function jobSummary(job) {
  if (!job) return "";
  const p = job.photos || {};
  const errors = (job.errors || []).length;
  if (job.status === "failed") return job.failure || "The photo job stopped";
  const done = job.revert
    ? `${p.reverted || 0} photo${p.reverted === 1 ? "" : "s"} back to the original`
    : `${p.enhanced || 0} photo${p.enhanced === 1 ? "" : "s"} enhanced`;
  return errors ? `${done} · ${errors} couldn't be done` : done;
}

export function JobProgress({ job }) {
  if (!job) return null;
  const pct = job.total ? Math.round((job.done / job.total) * 100) : 0;
  return (
    <div className="phx-progress" role="status">
      <div className="phx-progress-top">
        <span>
          {job.status === "running"
            ? `${job.revert ? "Reverting" : "Enhancing"} photos… ${job.done} of ${job.total} products`
            : jobSummary(job)}
        </span>
        <span>{pct}%</span>
      </div>
      <div className="phx-progress-bar">
        <span style={{ width: `${pct}%` }} />
      </div>
      {(job.errors || []).length > 0 && (
        <ul className="phx-errors">
          {job.errors.slice(0, 6).map((e, i) => (
            <li key={i}>
              {e.name || e.product_id}
              {e.photo ? ` (photo ${e.photo})` : ""}: {e.error}
            </li>
          ))}
          {job.errors.length > 6 && <li>…and {job.errors.length - 6} more</li>}
        </ul>
      )}
    </div>
  );
}

/* One sample photo: the enhanced preview, or the original while held down
   (or while "Show original" is on). */
function PreviewTile({ sample, before, after, loading, showOriginal, large }) {
  const [holding, setHolding] = useState(false);
  const src = showOriginal || holding ? before : after || before;
  return (
    <figure className={"phx-tile" + (large ? " phx-tile--large" : "")}>
      <div
        className="phx-tile-img"
        onPointerDown={() => setHolding(true)}
        onPointerUp={() => setHolding(false)}
        onPointerLeave={() => setHolding(false)}
        title="Hold to see the original"
      >
        {src ? <img src={src} alt={sample.name} draggable={false} /> : <div className="phx-tile-empty" />}
        {loading && <span className="adm-spinner phx-tile-spin" />}
        <span className="phx-tile-tag">{showOriginal || holding ? "Original" : "Enhanced"}</span>
      </div>
      <figcaption>{sample.name}</figcaption>
    </figure>
  );
}

/**
 * props:
 *   products       [{_id, name, images}] - all products to apply to (bulk) or the one product
 *   imageIndex     number - single-photo mode (product editor); omit for bulk
 *   initial        starting adjustments (single-photo mode: the photo's current ones)
 *   job            the running/finished job from useEnhanceJob (progress shown here)
 *   onApply(body)  starts the job; onClose()
 */
export default function RAdminPhotoEnhance({ products, imageIndex, initial, job, onApply, onClose, navigate }) {
  const single = imageIndex != null;
  const [adj, setAdj] = useState({ ...EMPTY_ADJUSTMENTS, ...(initial || {}) });
  const [scope, setScope] = useState("main");
  const [showOriginal, setShowOriginal] = useState(false);
  const [previews, setPreviews] = useState({}); // id -> {before, after, loading, error}
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  const urlsRef = useRef([]);
  const genRef = useRef(0);
  const running = job && job.status === "running";

  const samples = products
    .filter((p) => (p.images || []).length > (single ? imageIndex : 0))
    .slice(0, single ? 1 : 6);
  const sampleKey = samples.map((p) => p._id).join(",");
  const photoIndex = single ? imageIndex : 0;

  const render = useCallback(
    async (product, adjustments) => {
      const res = await adminFetch("/api/admin/images/preview", {
        method: "POST",
        navigate,
        body: JSON.stringify({ product_id: product._id, image_index: photoIndex, adjustments }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Preview failed");
      }
      const url = URL.createObjectURL(await res.blob());
      urlsRef.current.push(url);
      return url;
    },
    [navigate, photoIndex]
  );

  /* Originals, once */
  useEffect(() => {
    samples.forEach((p) => {
      render(p, {})
        .then((url) => setPreviews((prev) => ({ ...prev, [p._id]: { ...prev[p._id], before: url } })))
        .catch((err) => !err.isAuthError && setPreviews((prev) => ({ ...prev, [p._id]: { ...prev[p._id], error: err.message } })));
    });
  }, [sampleKey, render]);

  /* Enhanced previews, debounced while sliders move */
  useEffect(() => {
    const gen = ++genRef.current;
    if (isIdentity(adj)) {
      setPreviews((prev) => {
        const next = {};
        Object.keys(prev).forEach((id) => (next[id] = { ...prev[id], after: null, loading: false }));
        return next;
      });
      return undefined;
    }
    setPreviews((prev) => {
      const next = { ...prev };
      samples.forEach((p) => (next[p._id] = { ...next[p._id], loading: true }));
      return next;
    });
    const timer = setTimeout(() => {
      samples.forEach((p) => {
        render(p, adj)
          .then((url) => {
            if (gen !== genRef.current) return;
            setPreviews((prev) => ({ ...prev, [p._id]: { ...prev[p._id], after: url, loading: false } }));
          })
          .catch((err) => {
            if (gen !== genRef.current || err.isAuthError) return;
            setPreviews((prev) => ({ ...prev, [p._id]: { ...prev[p._id], loading: false, error: err.message } }));
          });
      });
    }, 350);
    return () => clearTimeout(timer);
  }, [adj, sampleKey, render]);

  useEffect(() => () => urlsRef.current.forEach((u) => URL.revokeObjectURL(u)), []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const set = (key, value) => setAdj((a) => ({ ...a, [key]: value }));
  const applyPreset = (values) => setAdj({ ...EMPTY_ADJUSTMENTS, ...values });

  const apply = async () => {
    setError("");
    setStarting(true);
    try {
      await onApply({
        product_ids: products.map((p) => p._id),
        scope: single ? "main" : scope,
        ...(single ? { image_index: imageIndex } : {}),
        adjustments: adj,
      });
    } catch (err) {
      if (!err.isAuthError) setError(err.message);
    } finally {
      setStarting(false);
    }
  };

  const count = products.length;
  // All-zero settings revert - offered in the editor for an enhanced photo
  // (the product list has its own "Revert to original" button)
  const canRevert = single && Boolean(photoEnhance((products[0].images || [])[imageIndex]));
  const photoWord = single ? "this photo" : scope === "main" ? `the main photo of ${count} product${count === 1 ? "" : "s"}` : `every photo of ${count} product${count === 1 ? "" : "s"}`;
  const sampleErrors = samples.map((p) => previews[p._id] && previews[p._id].error).filter(Boolean);

  return (
    <div className="adm-modal-overlay" onClick={() => !running && onClose()}>
      <div className="adm-modal phx-modal" role="dialog" aria-label="Enhance photos" onClick={(e) => e.stopPropagation()}>
        <div className="adm-modal-head">
          <div>
            <div className="adm-modal-title">{single ? "Adjust photo" : "Enhance photos"}</div>
            <div className="phx-sub">
              {single ? products[0].name : `${count} product${count === 1 ? "" : "s"} selected`} · rendered from the original photo, so you can always revert
            </div>
          </div>
          <button type="button" className="adm-modal-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="phx-layout">
          <div className="phx-previews">
            <div className="phx-previews-head">
              <span className="adm-seclbl">{single ? "Preview" : `Preview · ${samples.length} of ${count}`}</span>
              <label className="adm-switch">
                <input type="checkbox" checked={showOriginal} onChange={(e) => setShowOriginal(e.target.checked)} />
                <span className="adm-switch-track" aria-hidden="true" />
                <span>Show original</span>
              </label>
            </div>
            <div className={"phx-grid" + (single ? " phx-grid--single" : "")}>
              {samples.map((p) => {
                const pv = previews[p._id] || {};
                return (
                  <PreviewTile
                    key={p._id}
                    sample={p}
                    before={pv.before}
                    after={isIdentity(adj) ? null : pv.after}
                    loading={pv.loading || !pv.before}
                    showOriginal={showOriginal}
                    large={single}
                  />
                );
              })}
            </div>
            {sampleErrors.length > 0 && <div className="adm-note adm-note--amber phx-note">{sampleErrors[0]}</div>}
            <div className="phx-hint">Hold a photo to compare with the original.</div>
          </div>

          <div className="phx-controls">
            <div className="phx-presets">
              {PRESETS.map((p) => (
                <button key={p.key} type="button" className="phx-preset" onClick={() => applyPreset(p.values)} disabled={running}>
                  {p.label}
                </button>
              ))}
              <button type="button" className="phx-preset phx-preset--reset" onClick={() => applyPreset({})} disabled={running}>
                Reset all
              </button>
            </div>

            <label className="adm-switch phx-auto">
              <input type="checkbox" checked={adj.auto} onChange={(e) => set("auto", e.target.checked)} disabled={running} />
              <span className="adm-switch-track" aria-hidden="true" />
              <span>
                <strong>Auto tone</strong> — balances each photo's brightness and neutralises the backdrop colour
              </span>
            </label>

            {SLIDER_GROUPS.map((group) => (
              <div key={group.title} className="phx-group">
                <div className="adm-seclbl">{group.title}</div>
                {group.sliders.map((s) => (
                  <div key={s.key} className="phx-slider">
                    <div className="phx-slider-top">
                      <span>{s.label}</span>
                      <button type="button" className="phx-value" onClick={() => set(s.key, 0)} title="Reset" disabled={running}>
                        {adj[s.key] > 0 ? `+${adj[s.key]}` : adj[s.key]}
                      </button>
                    </div>
                    <input
                      type="range"
                      min={s.min != null ? s.min : -100}
                      max={100}
                      step={1}
                      value={adj[s.key]}
                      onChange={(e) => set(s.key, Number(e.target.value))}
                      onDoubleClick={() => set(s.key, 0)}
                      disabled={running}
                      aria-label={s.label}
                    />
                  </div>
                ))}
              </div>
            ))}

            {!single && (
              <div className="phx-group">
                <div className="adm-seclbl">Apply to</div>
                <div className="adm-tabs phx-scope">
                  <button type="button" className={"adm-tab" + (scope === "main" ? " is-active" : "")} onClick={() => setScope("main")} disabled={running}>
                    Main photo
                  </button>
                  <button type="button" className={"adm-tab" + (scope === "all" ? " is-active" : "")} onClick={() => setScope("all")} disabled={running}>
                    All photos
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {job && <JobProgress job={job} />}
        {error && <div className="adm-note adm-note--red phx-note">{error}</div>}

        <div className="adm-modal-actions">
          <button type="button" className="adm-btn adm-btn--ghost" onClick={onClose}>
            {running ? "Close (keeps running)" : job ? "Done" : "Cancel"}
          </button>
          <button type="button" className="adm-btn" onClick={apply} disabled={running || starting || (isIdentity(adj) && !canRevert)}>
            {running
              ? "Working…"
              : !isIdentity(adj)
              ? `Apply to ${photoWord}`
              : canRevert
              ? "Revert to original"
              : "Choose an adjustment"}
          </button>
        </div>
      </div>
    </div>
  );
}
